import React from 'react';
import { Camera, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EMPTY_FG, FgPicker } from '@/components/pr/FgPicker';
import { PendingForFg } from '@/components/pr/PendingForFg';
import { prApi, prErr, fmtQty } from '@/services/prApi';

export const emptyPrLine = () => ({ tempId: Math.random().toString(36).slice(2), ...EMPTY_FG,
  required_qty: '', unit: 'pcs', target_rate: '', remarks: '', image_urls: [], ordered_qty: 0 });

const F = ({ label, span, children }) => <div className={span}><label className="text-xs text-gray-500">{label}</label>{children}</div>;

/** One requirement line (no supplier): FG from the Rates Master, qty, unit, target rate, remarks, photos. */
export const PrLineEditor = ({ l, idx, onChange, onRemove, canRemove, excludePrId }) => {
  const upload = async (files) => {
    if (!files?.length) return;
    try {
      const r = await prApi.uploadPhotos(files);
      onChange({ image_urls: [...l.image_urls, ...(r.urls || [])] });
      toast.success(`${(r.urls || []).length} photo(s) uploaded`);
    } catch (e) { toast.error(prErr(e, 'Upload failed')); }
  };
  return (
    <div className="rounded-lg border bg-white p-3" data-testid={`pr-line-${idx}`}>
      <div className="mb-2 flex items-center justify-between">
        <Badge variant="outline">Line {idx + 1}</Badge>
        {l.ordered_qty > 0 && <span className="text-xs text-amber-700" data-testid={`pr-line-ordered-${idx}`}>{fmtQty(l.ordered_qty)} already on POs — the FG can't change</span>}
        {canRemove && <Button variant="ghost" size="sm" onClick={onRemove} className="h-7 text-red-500" data-testid={`pr-line-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
      <FgPicker line={l} idx={idx} locked={l.ordered_qty > 0} onChange={onChange} />
      <PendingForFg fg={l.mapped_fg_name} excludePrId={excludePrId} idx={idx} />
      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-12">
        <F label="Qty *" span="md:col-span-2"><Input type="number" min={l.ordered_qty || 0} value={l.required_qty} onChange={(e) => onChange({ required_qty: e.target.value })} data-testid={`pr-qty-${idx}`} /></F>
        <F label="Unit" span="md:col-span-2">
          <Select value={l.unit} onValueChange={(v) => onChange({ unit: v })}>
            <SelectTrigger data-testid={`pr-unit-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>{['pcs', 'mtr', 'kg', 'bundle'].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select></F>
        <F label="Target rate (optional)" span="md:col-span-2"><Input type="number" value={l.target_rate} onChange={(e) => onChange({ target_rate: e.target.value })} data-testid={`pr-rate-${idx}`} /></F>
        <F label="Remarks" span="md:col-span-6"><Input value={l.remarks} onChange={(e) => onChange({ remarks: e.target.value })} data-testid={`pr-remarks-${idx}`} /></F>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-2">
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-xs hover:border-blue-500">
          <Camera className="h-3.5 w-3.5" /><span>Add photo(s)</span>
          <input type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={(e) => upload(e.target.files)} data-testid={`pr-photo-${idx}`} />
        </label>
        {l.image_urls.map((u) => (
          <div key={u} className="group relative">
            <img src={u} alt="design" className="h-16 w-16 rounded border object-cover" />
            <button type="button" onClick={() => onChange({ image_urls: l.image_urls.filter((x) => x !== u) })} className="absolute -right-1.5 -top-1.5 rounded-full bg-red-500 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"><X className="h-3 w-3" /></button>
          </div>))}
      </div>
    </div>
  );
};

export const linePayload = (l) => ({ mapped_fg_name: l.mapped_fg_name, required_qty: Number(l.required_qty) || 0, unit: l.unit,
  target_rate: Number(l.target_rate) || 0, remarks: l.remarks || '', image_urls: l.image_urls || [] });

export default PrLineEditor;
